/* eslint-disable eqeqeq */
import React, { useEffect, useState, useRef, useCallback, useContext } from "react";
import { format, parse, isValid, add, sub } from "date-fns";
import { format as formatTz } from "date-fns-tz";
import Datetime from "react-datetime";
import axios from "axios";
import { confirmAlert } from "react-confirm-alert";
import { Navigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth.js";
import { domainConfig } from "../../config.js";
import {
  Container,
  Modal,
  Col,
  Form,
  Breadcrumb,
  Row,
  Tooltip,
  OverlayTrigger,
  Button,
  Badge,
  Collapse,
  InputGroup,
  Card,
  Dropdown
} from "react-bootstrap";
import { ColumnSortingTable } from "../../components/Table.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useLayout from "../../hooks/useLayout.js";
import {
  faSync,
  faEraser,
  faAngleDown,
  faCopy,
  faMagnifyingGlass,
  faServer,
  faCog
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { FileText } from "react-feather";
import Loader from "../../components/Loader.js";
import { PageLoader } from "../../components/Elements.js";
import { ShowConfirm } from "../../components/Alerts.js";
import {
  convertDateToDisplay,
  SelectColumnFilter,
} from "../../assets/functions.js";
import {
  getLevelInfo,
  getOriginInfo,
  getTypeInfo,
  getOriginsByCategory,
  getAllTypes,
  getLogFilterParams,
  getProjectName,
  LOG_PROJECT_OPTIONS,
  normalizeLogRow,
} from "../../assets/logMetadata.js";

import NotyfContext from "../../contexts/NotyfContext.js";

export function defaultInitialDate() {
  const dataObj = new Date();
  const dataObjX = sub(dataObj, { days: "7" });
  return formatTz(dataObjX, "yyyy-MM-dd HH:mm:ss");
}

export function defaultEndDate() {
  const dataObj = new Date();
  const dataObjX = add(dataObj, { days: "7" });
  return formatTz(dataObjX, "yyyy-MM-dd HH:mm");
}

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const formatUptime = (uptime) => {
  if (!uptime) return '0s';
  const seconds = Math.floor((Date.now() - uptime) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

// ─── Detail Modal ─────────────────────────────────────────────────────────────

const HealthCard = ({ name, status, dbStatus, errorMsg, processes }) => {
  const isLoading = status === "loading";
  const processList = Array.isArray(processes) ? processes : [];

  // Agrega as estatísticas dos processos deste servidor
  const hasProcesses = processList.length > 0;
  const totalCpu = hasProcesses ? processList.reduce((acc, p) => acc + (p.cpu || 0), 0) : 0;
  const totalMemory = hasProcesses ? processList.reduce((acc, p) => acc + (p.memory || 0), 0) : 0;
  const maxRestarts = hasProcesses ? Math.max(...processList.map(p => p.restarts || 0)) : 0;
  const maxUptime = hasProcesses ? Math.max(...processList.map(p => p.uptime || 0)) : 0;

  return (
    <Col md={4} sm={6} className="mb-3">
      <Card className="h-100 shadow-sm border">
        <Card.Body className="p-3">
          <div className={`d-flex align-items-center ${isLoading ? "mb-0" : "mb-3"}`}>
            <FontAwesomeIcon
              icon={faServer}
              className={`me-2 ${status === "ok" ? "text-success" : status === "error" ? "text-danger" : "text-muted"}`}
              size="sm"
            />
            <div className="flex-grow-1">
              <h6 className="mb-0 fw-bold" style={{ fontSize: "0.9rem" }}>
                {name}
              </h6>
              {processList?.[0]?.hostname && (
                <div className="text-muted mb-0" style={{ fontSize: "0.65rem" }}>
                  Host: {processList[0].hostname}
                </div>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="d-flex justify-content-center">
              <PageLoader color="#d7cd12" width="20" />
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="small text-muted mb-0">Serviço</span>
                <Badge
                  bg={
                    status === "ok"
                      ? "success"
                      : status === "error"
                        ? "danger"
                        : "warning"
                  }
                  className={"text-white"}
                  style={{ fontSize: "0.7rem" }}
                >
                  {status === "ok"
                    ? "Online"
                    : status === "error"
                      ? "Offline"
                      : "..."}
                </Badge>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="small text-muted mb-0">Banco de Dados</span>
                <Badge
                  bg={dbStatus === "ok" ? "success" : "danger"}
                  className="text-white"
                  style={{ fontSize: "0.7rem" }}
                >
                  {dbStatus === "ok" ? "Online" : "Offline"}
                </Badge>
              </div>

              {hasProcesses && (
                <div className="mt-2 pt-2 border-top">
                  <div className="small fw-semibold mb-1">PM2 Nodejs</div>
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted mb-0">CPU</span>
                    <span className="fw-bold text-dark">{totalCpu.toFixed(1)}%</span>
                  </div>
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted mb-0">Memória</span>
                    <span className="fw-bold text-dark">{formatBytes(totalMemory)}</span>
                  </div>
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted mb-0">Restarts</span>
                    <span className={`fw-bold ${maxRestarts > 5 ? 'text-danger' : 'text-dark'}`}>
                      {maxRestarts}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted mb-0">Uptime</span>
                    <span className="fw-bold text-dark">{formatUptime(maxUptime)}</span>
                  </div>

                  {processList.map((p, i) => (
                    <div key={`pm2-${p.pm_id ?? i}-${p.pid ?? 0}`} className="small mb-1 mt-2">
                      <div className="text-muted mb-0"><b>{p.name}</b></div>
                      <div className="text-muted mb-0">ID: {p.pm_id} | PID: {p.pid} | Status: {p.status}</div>
                      <div className="text-muted mb-0">CPU: {p.cpu}% | Memoria: {formatBytes(p.memory)} | Restarts: {p.restarts} | Uptime: {formatUptime(p.uptime)}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

const LogModal = ({ openModals, dataModal, handleClose }) => {
  const notyf = useContext(NotyfContext);
  if (!dataModal) return null;

  const projectId = dataModal.projeto || 0;
  const levelInfo = getLevelInfo(dataModal.level);
  const originInfo = getOriginInfo(projectId, dataModal.rawOrigem || dataModal.origem);
  const typeInfo = getTypeInfo(projectId, dataModal.rawType || dataModal.type);

  const isObject =
    dataModal.erro &&
    typeof dataModal.erro === "object" &&
    !Array.isArray(dataModal.erro) &&
    Object.keys(dataModal.erro).length > 0;

  return (
    <Modal show={openModals} onHide={handleClose} size="lg" fullscreen="sm-down" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Log #{dataModal.id}
          <br /><small className="text-muted"><b>Tipo:</b> <span className={`badge bg-${levelInfo.badge} ms-1`}>{levelInfo.label}</span></small>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <h6 className="text-uppercase fw-bold">Origem</h6>
        <Row className="mb-4">
          <p className="mb-0"><strong>Projeto:</strong> <span className="opacity-75">{dataModal.projectLabel || getProjectName(dataModal.projeto)}</span></p>
          <p className="mb-0"><strong>Data:</strong> <span className="opacity-75">{dataModal.data}</span></p>
          <p className="mb-0"><strong>Identificador:</strong> <span className="opacity-75">{dataModal.rawOrigem || dataModal.origem}</span></p>

          {originInfo?.label && (
            <p className="mb-0"><strong>Label:</strong> <span className="opacity-75">{originInfo.label}</span></p>
          )}
          {originInfo?.description && (
            <p className="mb-0"><strong>Descrição:</strong> <span className="opacity-75">{originInfo.description}</span></p>
          )}
          {originInfo?.file && (
            <p className="mb-0"><strong>Arquivo:</strong> <span className="opacity-75">{originInfo.file}</span></p>
          )}
          {originInfo?.route && (
            <p className="mb-0"><strong>Rota:</strong> <span className="opacity-75">{originInfo.route}</span></p>
          )}
        </Row>

        <h6 className="text-uppercase fw-bold mt-4">Tipo de Operação</h6>
        <Row className="mb-4">
          <p className="mb-0"><strong>Identificador:</strong> <span className="opacity-75">{dataModal.rawType || dataModal.type}</span></p>

          {typeInfo?.label && (
            <p className="mb-0"><strong>Rótulo:</strong> <span className="opacity-75">{typeInfo.label}</span></p>
          )}
          {typeInfo?.description && (
            <p className="mb-0"><strong>Descrição:</strong> <span className="opacity-75">{typeInfo.description}</span></p>
          )}
          {typeInfo?.affectedArea && (
            <p className="mb-0"><strong>Área afetada:</strong> <span className="opacity-75">{typeInfo.affectedArea}</span></p>
          )}
        </Row>

        {(dataModal.evento_id || dataModal.event_id || dataModal.visitante_id || dataModal.user_id || dataModal.customizacao_id || dataModal.user_email) && (
          <>
            <h6 className="text-uppercase fw-bold mt-3">Contexto</h6>
            <Row className="mb-4">
              {dataModal.evento_id && (
                <p className="mb-0"><strong>Evento ID:</strong> <span className="opacity-75">{dataModal.evento_id}</span></p>
              )}
              {dataModal.event_id && (
                <p className="mb-0"><strong>Event ID:</strong> <span className="opacity-75">{dataModal.event_id}</span></p>
              )}
              {dataModal.visitante_id && (
                <p className="mb-0"><strong>Visitante ID:</strong> <span className="opacity-75">{dataModal.visitante_id}</span></p>
              )}
              {dataModal.user_id && (
                <p className="mb-0"><strong>Usuário ID:</strong> <span className="opacity-75">{dataModal.user_id}</span></p>
              )}
              {dataModal.user_email && (
                <p className="mb-0"><strong>E-mail:</strong> <span className="opacity-75">{dataModal.user_email}</span></p>
              )}
              {dataModal.customizacao_id && (
                <p className="mb-0"><strong>Customizacao ID:</strong> <span className="opacity-75">{dataModal.customizacao_id}</span></p>
              )}
            </Row>
          </>
        )}

        <h6 className="text-uppercase fw-bold mt-3 mb-2">
          Payload do Log
          <FontAwesomeIcon
            icon={faCopy}
            className="text-muted cursor-pointer ms-2"
            onClick={() => {
              const rawJson = dataModal.rawErro || (typeof dataModal.erro === "string" ? dataModal.erro : JSON.stringify(dataModal.erro, null, 2));
              navigator.clipboard.writeText(rawJson);
              notyf.open({
                type: "success",
                message: 'Log copiado com sucesso!',
                ripple: true,
                dismissible: true,
              });
            }}
            title="Copiar Payload"
          />
        </h6>
        {isObject ? (
          <div className="rounded p-3 mb-3 bg-dark text-light" style={{ fontFamily: 'monospace', fontSize: '0.85rem', overflowX: 'auto' }}>
            {Object.entries(dataModal.erro).map(([key, value], index) => (
              <div key={index} className="mb-1">
                <span style={{ color: '#7dd3fc' }}>{key}</span>
                <span style={{ color: '#94a3b8' }}>: </span>
                <span style={{ color: '#fde68a' }}>{JSON.stringify(value)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded p-3 mb-3 bg-dark text-light" style={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {typeof dataModal.erro === "string" ? dataModal.erro : dataModal.rawErro || "Sem dados disponíveis"}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const TableLogs = () => {
  const { tipo } = useAuth();
  const { setLoading } = useLayout();
  const notyf = useContext(NotyfContext);
  const [dataLogs, setDataLogs] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10, pageCount: 1, total: 0 });
  const [updateDataTable, setDataTableUpdate] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [expandFilters, setExpandFilters] = useState(false);

  const [filter, setFilter] = useState({
    project: 0,
    data_inicio: convertDateToDisplay(defaultInitialDate()),
    data_final: convertDateToDisplay(defaultEndDate()),
    level: 'todos',
    viewed: 'todos',
    origem: '',
    type: ''
  });

  const [dateValidation, setDateValidation] = useState({
    data_inicio: { isValid: true, error: null },
    data_final: { isValid: true, error: null }
  });

  const [healthLoading, setHealthLoading] = useState(true);
  const [healthStatus, setHealthStatus] = useState({});
  const [dataModal, setDataModal] = useState(false);
  const [openModals, setShowModal] = useState(false);

  const fetchHealth = async () => {
    setHealthLoading(true);

    const endpoints = [
          { id: "painel", name: "API Painel", url: domainConfig.restAPI },
          { id: "apps", name: "API WebApps", url: domainConfig.aplicativosAPI },
          { id: "eventos", name: "API Networking", url: domainConfig.eventosAPI },
          { id: "creden", name: "API Credenciamento", url: domainConfig.credenciamentoAPI }
    ];

    const results = {};

    await Promise.allSettled(
      endpoints.map(async (ep) => {
        if (!ep.url) {
          results[ep.id] = { status: "error", dbStatus: "error", errorMsg: "URL não configurada" };
          return;
        }

        try {
          let baseUrl = ep.url.replace(/\/$/, "");
          if (baseUrl.startsWith("wss://")) {
            baseUrl = baseUrl.replace("wss://", "https://");
          } else if (baseUrl.startsWith("ws://")) {
            baseUrl = baseUrl.replace("ws://", "http://");
          }

          const response = await axios.get(`${baseUrl}/api/health`, {
            timeout: 10000,
            // The page renders its own endpoint status; do not duplicate an
            // expected timeout/offline state as client-error telemetry.
            __r1SkipTelemetry: true,
          });
          if (response.data.status === "ok") {
            results[ep.id] = {
              status: "ok",
              dbStatus: response.data.database === "connected" ? "ok" : "error",
              processes: (response.data.processes || []).map(p => ({ ...p, hostname: response.data.hostname, serverName: response.data.serverName })),
              errorMsg: null
            };
          } else {
            results[ep.id] = {
              status: "error",
              dbStatus: "error",
              errorMsg: response.data.message || "Erro desconhecido"
            };
          }
        } catch (error) {
          results[ep.id] = { status: "error", dbStatus: "error", errorMsg: error.message };
        }
      })
    );

    setHealthStatus(results);
    setHealthLoading(false);
  };

  const logsRef = useRef([]);

  const handlePaginationChange = useCallback(({ pageIndex, pageSize }) => {
    setPagination(prev => (
      prev.pageIndex === pageIndex && prev.pageSize === pageSize
        ? prev
        : { ...prev, pageIndex, pageSize }
    ));
  }, []);

  // Fire & forget view tracking mechanism. Keep the unread filter read-only so
  // refetching after a viewed update cannot drain the entire unread queue.
  const handlePageViewed = useCallback((pageRows) => {
    if (filter.viewed === '0') return;

    const unreadRows = pageRows.filter(row => row.viewed === 0 && row.id);
    if (unreadRows.length === 0) return;

    const logs = logsRef.current;
    const unreadByProject = {};
    const viewedKeys = new Set();

    unreadRows.forEach(row => {
      const proj = row.projeto;
      if (!unreadByProject[proj]) unreadByProject[proj] = [];
      unreadByProject[proj].push(row.id);
      viewedKeys.add(`${proj}:${row.id}`);
    });

    const updatedLogs = logs.map(log => (
      viewedKeys.has(`${log.projeto}:${log.id}`)
        ? { ...log, viewed: 1 }
        : log
    ));
    logsRef.current = updatedLogs;
    setDataLogs(updatedLogs);

    Promise.all(
      Object.entries(unreadByProject).map(([proj, ids]) => (
        axios
          .put("/api/logs/viewed", { ids: ids, project: proj })
          .then(() => true)
          .catch(e => {
            console.log(e);
            return false;
          })
      ))
    ).then(results => {
      if (results.some(Boolean)) window.dispatchEvent(new Event("logsViewed"));
    });
  }, [filter.viewed]);

  // Date validation helper function
  const validateDate = (dateString, fieldName) => {
    if (!dateString || dateString.trim() === '') return { isValid: false, error: 'Data é obrigatória' };
    try {
      const parsedDate = parse(dateString, 'dd/MM/yyyy HH:mm', new Date());
      if (!isValid(parsedDate)) return { isValid: false, error: 'Formato de data inválido' };
      if (fieldName === 'data_final' && filter.data_inicio) {
        try {
          const startDate = parse(filter.data_inicio, 'dd/MM/yyyy HH:mm', new Date());
          if (isValid(startDate) && parsedDate < startDate) {
            return { isValid: false, error: 'Data final deve ser posterior à data inicial' };
          }
        } catch { }
      }
      return { isValid: true, error: null };
    } catch {
      return { isValid: false, error: 'Formato de data inválido' };
    }
  };

  const updateDateValidation = () => {
    const startValidation = validateDate(filter.data_inicio, 'data_inicio');
    const endValidation = validateDate(filter.data_final, 'data_final');
    setDateValidation({ data_inicio: startValidation, data_final: endValidation });
    return startValidation.isValid && endValidation.isValid;
  };

  const deleteLogHistory = () => {
    if (!updateDateValidation()) return;

    setLoading(true);
    axios
      .delete("/api/logs", {
        params: {
          scope: "global",
          project: filter.project,
          data_inicio: filter.data_inicio,
          data_final: filter.data_final,
        },
      })
      .then(() => {
        logsRef.current = [];
        setDataLogs([]);
        setPagination(prev => ({ ...prev, pageIndex: 0, pageCount: 1, total: 0 }));
        window.dispatchEvent(new Event("logsViewed"));
        notyf.open({
          type: "success",
          message: "Histórico de logs excluído com sucesso",
          ripple: true,
          dismissible: true,
        });
      })
      .catch((error) => {
        console.log(error);
        notyf.open({
          type: "danger",
          message: "Não foi possível excluir o histórico de logs",
          ripple: true,
          dismissible: true,
        });
      })
      .finally(() => setLoading(false));
  };

  const handleDeleteLogHistory = () => {
    if (!updateDateValidation()) return;

    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Confirmar ação"
          description={`Ao continuar, os logs sem evento entre ${filter.data_inicio} e ${filter.data_final} serão excluídos.`}
          action={() => {
            deleteLogHistory();
            onClose();
          }}
          onClose={onClose}
        />
      ),
    });
  };

  useEffect(() => {
    updateDateValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.data_inicio, filter.data_final]);

  useEffect(() => {
    const isValidDates = updateDateValidation();
    if (!isValidDates) {
      console.warn('Invalid dates - skipping API call');
      return;
    }

    if (dataLogs !== false) {
      setLoading(true);
    }

    axios.get("/api/logs", {
      params: {
        scope: "global",
        project: filter.project,
        data_inicio: filter.data_inicio,
        data_final: filter.data_final,
        level: filter.level,
        viewed: filter.viewed,
        ...getLogFilterParams(filter.origem, filter.type),
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
      },
    })
      .then(function (response) {
        const logs = (response.data.message || []).map(element => {
          const normalizedLog = normalizeLogRow(element, { convertDate: convertDateToDisplay });
          return {
            ...normalizedLog,
            icons: (
              <OverlayTrigger overlay={<Tooltip>Ver detalhes</Tooltip>}>
                <FileText
                  role="button"
                  className="align-middle cursor-pointer"
                  onClick={() => {
                    setDataModal(normalizedLog);
                    setShowModal(true);
                  }}
                  size={18}
                />
              </OverlayTrigger>
            ),
          };
        });
        logsRef.current = logs;
        setDataLogs(logs);
        setPagination(prev => ({
          ...prev,
          pageCount: Number(response.data.pagination?.page_count || 1),
          total: Number(response.data.pagination?.total || logs.length),
        }));
        setLoading(false);
      })
      .catch(function (error) {
        console.log(error);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDataTable, filter.project, filter.level, filter.viewed, filter.origem, filter.type, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    fetchHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDataTable]);

  if (tipo !== 1) {
    return <Navigate to="/" />;
  }

  if (dataLogs === false) {
    return <Loader />;
  }

  const selectedMetadataProject = parseInt(filter.project, 10) || 0;
  const originsByCategory = getOriginsByCategory(selectedMetadataProject);
  const allTypes = getAllTypes(selectedMetadataProject);

  const tableColumns = [
    {
      Header: "Projeto",
      accessor: "projeto",
      Filter: SelectColumnFilter,
      filter: "equals",
      renderFilterLabel: (val) => getProjectName(val),
      Cell: ({ value, row }) => {
        const isViewed = row.original.viewed === 1;
        return (
          <div className="d-flex align-items-center" style={{ minWidth: '150px' }}>
            {!isViewed && (
              <span
                className="bg-primary rounded-circle d-inline-block me-2"
                style={{ width: "10px", height: "10px", flexShrink: 0 }}
                title="Novo log não visualizado"
              />
            )}
            <span className="text-wrap">{row.original.projectLabel || getProjectName(value)}</span>
          </div>
        );
      }
    },
    {
      Header: "ID",
      accessor: "id",
      Cell: ({ value, row }) => {
        return (
          <div className="d-flex align-items-center">
            <span className="text-nowrap">{value}</span>
          </div>
        );
      },
    },
    { Header: "Operação ID", accessor: "operationId" },
    {
      Header: "Tipo",
      accessor: "level",
      Filter: SelectColumnFilter,
      filter: "equals",
      renderFilterLabel: (val) => getLevelInfo(val).label,
      Cell: ({ row }) => {
        const info = getLevelInfo(row.original.level);
        return (
          <Badge bg={info.badge} className="text-uppercase" style={{ fontSize: '0.7rem' }}>
            {info.label}
          </Badge>
        );
      }
    },
    { Header: "Resumo do log", accessor: "json" },
    { Header: "Origem", accessor: "origem", Filter: SelectColumnFilter, filter: "equals" },
    { Header: "Tipo de Operação", accessor: "type", Filter: SelectColumnFilter, filter: "equals" },
    { Header: "Data", accessor: "data" },
    { Header: "", accessor: "icons", disableSortBy: true, Filter: false },
  ];

  return (
    <Container fluid className="p-0">
      <Button
        type="button"
        variant="primary"
        className={`btn update-side-panel-button`}
        onClick={() => setDataTableUpdate(prev => prev + 1)}
      >
        <FontAwesomeIcon icon={faSync} size={"lg"} />
      </Button>
      <div className="mb-4">
        <Breadcrumb>
          <Breadcrumb.Item active>Logs de Sistema</Breadcrumb.Item>
        </Breadcrumb>
        <h6 className="text-muted mb-0">Status do Logs de ações e erros de todo o sistema</h6>
      </div>

      <Form.Group as={Col} className="textHeaderAplicativo mb-3">
        <h5 className="card-title mb-0">
          APIs
        </h5>
      </Form.Group>

      <Row className="mb-4 mx-0">
        <HealthCard
          name="API Painel"
          status={healthLoading ? "loading" : healthStatus.painel?.status}
          dbStatus={healthLoading ? "loading" : healthStatus.painel?.dbStatus}
          processes={healthStatus.painel?.processes}
          errorMsg={healthStatus.painel?.errorMsg}
        />
        <HealthCard
          name="API WebApps"
          status={healthLoading ? "loading" : healthStatus.apps?.status}
          dbStatus={healthLoading ? "loading" : healthStatus.apps?.dbStatus}
          processes={healthStatus.apps?.processes}
          errorMsg={healthStatus.apps?.errorMsg}
        />
        <HealthCard
          name="API Networking"
          status={healthLoading ? "loading" : healthStatus.eventos?.status}
          dbStatus={healthLoading ? "loading" : healthStatus.eventos?.dbStatus}
          processes={healthStatus.eventos?.processes}
          errorMsg={healthStatus.eventos?.errorMsg}
        />
        <HealthCard
          name="API Credenciamento"
          status={healthLoading ? "loading" : healthStatus.creden?.status}
          dbStatus={healthLoading ? "loading" : healthStatus.creden?.dbStatus}
          processes={healthStatus.creden?.processes}
          errorMsg={healthStatus.creden?.errorMsg}
        />
      </Row>

      <Form.Group as={Col} className="textHeaderAplicativo mb-3 d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">
          Histórico de logs ({pagination.total})
        </h5>
        <div className="d-flex align-items-center">
          <Dropdown className="d-inline">
            <Dropdown.Toggle className="btn btn-outline-primary">
              <FontAwesomeIcon icon={faCog} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                disabled={pagination.total === 0 || !dateValidation.data_inicio.isValid || !dateValidation.data_final.isValid}
                onClick={handleDeleteLogHistory}
              >
                Excluir histórico de logs
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Form.Group>

      {/* ── Main Filters Row ── */}
      <div className="mb-4">
        <Row className="mt-3 mx-0 gx-2">
          <Form.Group className="mb-3" as={Col} xs={6} sm={6} md="4">
            <Form.Label>Projeto</Form.Label>
            <Form.Select
              value={filter.project}
              onChange={(e) => {
                setFilter(prev => ({ ...prev, project: e.target.value, origem: '', type: '' }));
                setPagination(prev => ({ ...prev, pageIndex: 0 }));
                setDataTableUpdate(prev => prev + 1);
              }}
            >
              {LOG_PROJECT_OPTIONS.map((project) => (
                <option key={`project-${project.value}`} value={project.value}>{project.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" as={Col} xs={6} sm={6} md="4">
            <Form.Label>Tipo</Form.Label>
            <Select
              options={[
                { value: 'todos', label: 'Todos os tipos', color: 'bg-secondary' },
                { value: 'info', label: 'Informação', color: 'bg-info' },
                { value: 'success', label: 'Sucesso', color: 'bg-success' },
                { value: 'warn', label: 'Aviso', color: 'bg-warning' },
                { value: 'error', label: 'Erro', color: 'bg-danger' },
              ]}
              value={{
                value: filter.level,
                label: filter.level === 'todos' ? 'Todos os tipos' :
                  filter.level === 'info' ? 'Informação' :
                    filter.level === 'success' ? 'Sucesso' :
                      filter.level === 'warn' ? 'Aviso' :
                        filter.level === 'error' ? 'Erro' : '',
                color: filter.level === 'todos' ? 'bg-secondary' :
                  filter.level === 'info' ? 'bg-info' :
                    filter.level === 'success' ? 'bg-success' :
                      filter.level === 'warn' ? 'bg-warning' :
                        filter.level === 'error' ? 'bg-danger' : 'bg-secondary'
              }}
              onChange={(opt) => {
                setFilter(prev => ({ ...prev, level: opt.value }));
                setPagination(prev => ({ ...prev, pageIndex: 0 }));
                setDataTableUpdate(prev => prev + 1);
              }}
              formatOptionLabel={({ label, color }) => (
                <div className="d-flex align-items-center">
                  <span className={`${color} rounded-circle d-inline-block me-2`} style={{ width: '10px', height: '10px' }}></span>
                  {label}
                </div>
              )}
              styles={{
                control: (base) => ({
                  ...base,
                  height: 32,
                  minHeight: 32,
                }),
                valueContainer: (base) => ({
                  ...base,
                  height: 32,
                  padding: '0 6px',
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  height: 32,
                }),
                option: (base) => ({
                  ...base,
                  padding: '7px 10px',
                }),
              }}
              classNamePrefix="react-select"
              isSearchable={false}
            />
          </Form.Group>

          <Col md={2} className="d-flex align-items-end mb-3">
            <Button
              variant="primary"
              style={{ height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => {
                setFilter(prev => ({
                  ...prev,
                  level: 'todos',
                  viewed: 'todos',
                  data_inicio: convertDateToDisplay(defaultInitialDate()),
                  data_final: convertDateToDisplay(defaultEndDate()),
                  origem: '',
                  type: ''
                }));
                setResetKey(prev => prev + 1);
                setPagination(prev => ({ ...prev, pageIndex: 0 }));
                setDataTableUpdate(prev => prev + 1);
              }}
              title="Limpar filtros"
            >
              <FontAwesomeIcon icon={faEraser} />
            </Button>
          </Col>

        </Row>

        <Row className="mx-0 gx-2">
          <Col xs={12}>
            <div
              className="d-inline-flex align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setExpandFilters(!expandFilters)}
              aria-controls="filters-collapse"
              aria-expanded={expandFilters}
            >
              <span className="text-muted fw-semibold mb-0 me-2" style={{ fontSize: '0.9rem' }}>Mais filtros</span>
              <FontAwesomeIcon
                icon={faAngleDown}
                className="text-muted"
                style={{
                  transform: expandFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out'
                }}
              />
            </div>
          </Col>
        </Row>

        {/* ── Collapse Additional Filters ── */}
        <Collapse in={expandFilters}>
          <div id="filters-collapse">
            <div className="card shadow-none bg-transparent border mb-4">
              <div className="card-body">
                <Row className="mx-0 gx-2">
                  <Form.Group className="mb-3" as={Col} xs={12} sm={6} md="4">
                    <Form.Label>Origem</Form.Label>
                    <Form.Select
                      value={filter.origem || ''}
                      onChange={(e) => {
                        setFilter(prev => ({ ...prev, origem: e.target.value }));
                        setPagination(prev => ({ ...prev, pageIndex: 0 }));
                        setDataTableUpdate(prev => prev + 1);
                      }}
                    >
                      <option value="">Todas as origens</option>
                      {Object.entries(originsByCategory)
                        .sort(([catA], [catB]) => catA.localeCompare(catB))
                        .map(([category, items]) => (
                          <optgroup key={category} label={category}>
                            {items
                              .sort((a, b) => a.label.localeCompare(b.label))
                              .map(item => (
                                <option key={item.value} value={item.value}>{item.label}</option>
                              ))}
                          </optgroup>
                        ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3" as={Col} xs={12} sm={6} md="4">
                    <Form.Label>Tipo de Operação</Form.Label>
                    <Form.Select
                      value={filter.type || ''}
                      onChange={(e) => {
                        setFilter(prev => ({ ...prev, type: e.target.value }));
                        setPagination(prev => ({ ...prev, pageIndex: 0 }));
                        setDataTableUpdate(prev => prev + 1);
                      }}
                    >
                      <option value="">Todos as operações</option>
                      {[...allTypes]
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map(item => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3" as={Col} xs={12} sm={6} md="4">
                    <Form.Label>Status de leitura</Form.Label>
                    <Form.Select
                      value={filter.viewed}
                      onChange={(e) => {
                        setFilter(prev => ({ ...prev, viewed: e.target.value }));
                        setPagination(prev => ({ ...prev, pageIndex: 0 }));
                        setDataTableUpdate(prev => prev + 1);
                      }}
                    >
                      <option value="todos">Todos</option>
                      <option value="1">Lidos</option>
                      <option value="0">Não lidos</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3" as={Col} xs={12} sm={6} md="4">
                    <Form.Label>Data de início</Form.Label>
                    <InputGroup className="w-100 flex-nowrap">
                      <div className="flex-fill">
                        <div className={`position-relative ${!dateValidation.data_inicio.isValid ? 'is-invalid' : ''}`}>
                          <Datetime
                            value={filter.data_inicio}
                            onChange={(date) => {
                              if (date && typeof date === 'object' && date._isValid !== false) {
                                setFilter(prev => ({ ...prev, data_inicio: format(new Date(date), 'dd/MM/yyyy HH:mm') }));
                              } else if (typeof date === 'string') {
                                setFilter(prev => ({ ...prev, data_inicio: date }));
                              }
                            }}
                            dateFormat="DD/MM/YYYY"
                            timeFormat="HH:mm"
                            inputProps={{
                              placeholder: 'dd/MM/yyyy HH:mm',
                              className: `form-control ${!dateValidation.data_inicio.isValid ? 'is-invalid' : ''}`,
                              style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                            }}
                            locale="pt-br"
                          />
                        </div>
                      </div>
                      <InputGroup.Text onClick={() => {
                        setPagination(prev => ({ ...prev, pageIndex: 0 }));
                        setDataTableUpdate((prevCount) => prevCount + 1);
                      }} style={{ cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                      </InputGroup.Text>
                    </InputGroup>
                    {!dateValidation.data_inicio.isValid && (
                      <div className="invalid-feedback d-block">{dateValidation.data_inicio.error}</div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3" as={Col} xs={12} sm={6} md="4">
                    <Form.Label>Data de término</Form.Label>
                    <InputGroup className="w-100 flex-nowrap">
                      <div className="flex-fill">
                        <div className={`position-relative ${!dateValidation.data_final.isValid ? 'is-invalid' : ''}`}>
                          <Datetime
                            value={filter.data_final}
                            onChange={(date) => {
                              if (date && typeof date === 'object' && date._isValid !== false) {
                                setFilter(prev => ({ ...prev, data_final: format(new Date(date), 'dd/MM/yyyy HH:mm') }));
                              } else if (typeof date === 'string') {
                                setFilter(prev => ({ ...prev, data_final: date }));
                              }
                            }}
                            dateFormat="DD/MM/YYYY"
                            timeFormat="HH:mm"
                            inputProps={{
                              placeholder: 'dd/MM/yyyy HH:mm',
                              className: `form-control ${!dateValidation.data_final.isValid ? 'is-invalid' : ''}`,
                              style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
                            }}
                            locale="pt-br"
                            isValidDate={(current) => {
                              if (filter.data_inicio) {
                                try {
                                  const startDate = parse(filter.data_inicio, 'dd/MM/yyyy HH:mm', new Date());
                                  if (isValid(startDate)) {
                                    return current.toDate() >= startDate;
                                  }
                                } catch {
                                  return true;
                                }
                              }
                              return true;
                            }}
                          />
                        </div>
                      </div>
                      <InputGroup.Text onClick={() => {
                        setPagination(prev => ({ ...prev, pageIndex: 0 }));
                        setDataTableUpdate((prevCount) => prevCount + 1);
                      }} style={{ cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                      </InputGroup.Text>
                    </InputGroup>
                    {!dateValidation.data_final.isValid && (
                      <div className="invalid-feedback d-block">{dateValidation.data_final.error}</div>
                    )}
                  </Form.Group>

                  {/* Removed reset button from here */}
                </Row>
              </div>
            </div>
          </div>
        </Collapse>
      </div>
      <Row>
        <Col md={12}>
          <ColumnSortingTable
            key={resetKey}
            columns={tableColumns}
            data={dataLogs}
            getCellProps={(cellinfo) => ({
              className: cellinfo.column.id === "icons" ? "table-icons" : "",
              style: {
                maxWidth: cellinfo.column.id === "json" ? "500px" : "",
              },
            })}
            onPageChange={handlePageViewed}
            onPaginationChange={handlePaginationChange}
            manualPagination
            serverPageCount={pagination.pageCount}
            totalRows={pagination.total}
          />
          <LogModal
            openModals={openModals}
            dataModal={dataModal}
            handleClose={() => setShowModal(false)}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default TableLogs;

